import { PropsWithChildren, ReactElement, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { Button, Center, Loader, Stack } from "@mantine/core";
import { AddOnsCatalogContext } from "@stores/addons/context";
import { useGetAddOns } from "@stores/addons/store";
import { ConfigContext } from "@stores/config/context";
import { useGetConfig } from "@stores/config/store";
import { PlansContext } from "@stores/plans/context";
import { useGetPlans } from "@stores/plans/store";
import { getPlansHierarchy } from "@stores/plans/utils";
import { AccountContext } from "@stores/session/context";
import { sessionStore } from "@stores/session/store";
import { redirectBySession } from "@stores/session/utils";
import Sentry from "@common/components/thirdParty/Sentry";
import ErrorMessage from "@common/components/text/ErrorMessage";

export default function AppDataProvider({
  children,
}: PropsWithChildren): ReactElement {
  const router = useRouter();
  const {
    isLoading: loadingAccount,
    isFetched: accountFetched,
    error: accountError,
    data: account,
  } = sessionStore.useGetAccount({
    enabled: true,
    refetchInterval: 1000 * 60 * 5, // reload every 5 minutes
  });

  useEffect(() => {
    if (!loadingAccount) {
      redirectBySession(router, account, accountError);
    }
  }, [loadingAccount, router, account, accountError]);

  const {
    data: config,
    error: configError,
    refetch: refetchConfig,
  } = useGetConfig();
  const {
    data: plans,
    error: plansError,
    refetch: refetchPlans,
  } = useGetPlans();
  const {
    data: addons,
    error: addonsError,
    refetch: refetchAddons,
  } = useGetAddOns();

  const accountValue = useMemo(() => ({ account }), [account]);
  const plansValue = useMemo(
    () => ({
      plans: plans ?? [],
      plansHierarchy: getPlansHierarchy(plans ?? []),
    }),
    [plans],
  );
  const ready = accountFetched && !!config && !!plans && !!addons;
  const catalogError = configError ?? plansError ?? addonsError;

  if (catalogError) {
    return (
      <Center w="100%" h="100vh">
        <Stack align="center">
          <ErrorMessage fw="bold" error={catalogError} />
          <Button
            variant="outline"
            onClick={() => {
              refetchConfig();
              refetchPlans();
              refetchAddons();
            }}
          >
            Retry
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!ready) {
    return (
      <Center w="100%" h="100vh">
        <Loader />
      </Center>
    );
  }

  return (
    <AccountContext.Provider value={accountValue}>
      <ConfigContext.Provider value={config}>
        <PlansContext.Provider value={plansValue}>
          <AddOnsCatalogContext.Provider value={addons ?? []}>
            {children}

            <Sentry account={account && { id: account.id }} />
          </AddOnsCatalogContext.Provider>
        </PlansContext.Provider>
      </ConfigContext.Provider>
    </AccountContext.Provider>
  );
}
